import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import {
  Checkbox,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import type { FieldErrors } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';

import { getPblCase, submitPblEvaluation } from '@/api/pbl.api';
import PageContainer from '@/components/Container/PageContainer';
import RatingScale from '@/components/Pbl/RatingScale';
import { Button } from '@/components/UI/Button';
import { SignatureModal } from '@/components/UI/Modal';
import useResize from '@/hooks/useResize';
import PATH from '@/router/path';
import type { PblCase, PblMeeting } from '@/types/pbl';
import { errorHandler, successHandler } from '@/utils/toastHandler';

import { mapPblCaseToOcManagedCase } from '../OcManageCases/caseReviewState';
import { TitleBar } from '../OcManageCases/styles';
import {
  ActionRow,
  BodyText,
  ChoiceInputSlot,
  ChoiceWithInput,
  Divider,
  FeedbackShell,
  FieldGroup,
  FieldLabel,
  InfoGrid,
  InfoLabel,
  InfoValue,
  PartTitle,
  RatingBlock,
  RatingLabel,
  SectionTitle,
  SignaturePreview,
  SignaturePreviewAction,
  SignaturePreviewImage,
  SignatureRow,
  SignedBadge,
  ValidationText,
} from './styles';
import { useTranslation } from 'react-i18next';

const categories = ['Estate Planning', 'Family', 'Employment', 'Building Management', 'Tenancy', 'Criminal', 'Other'];

const ratingQuestionKeys = [
  { key: 'ease_of_platform', labelKey: 'pbl.pagesHardcoded.lawyerCaseFeedback.easeOfUsingEbramDmpPlatform' },
  { key: 'case_information_clarity', labelKey: 'pbl.pagesHardcoded.lawyerCaseFeedback.clarityOfEnquirerSCaseInformation' },
  { key: 'pre_training_usefulness', labelKey: 'pbl.pagesHardcoded.lawyerCaseFeedback.usefulnessOfPreTrainingSession' },
  { key: 'communication_connection_quality', labelKey: 'pbl.pagesHardcoded.lawyerCaseFeedback.qualityOfCommunicationConnectionViaDmp' },
  { key: 'case_management_facilities', labelKey: 'pbl.pagesHardcoded.lawyerCaseFeedback.effectivenessOfDmpFacilitiesToCaseManagement' },
  { key: 'technical_support_quality', labelKey: 'pbl.pagesHardcoded.lawyerCaseFeedback.qualityAndPromptnessOfTechnicalSupport' },
  { key: 'overall_experience', labelKey: 'pbl.pagesHardcoded.lawyerCaseFeedback.overallExperienceUsingDmp' },
] as const;

const invalidFieldOrder = [
  'duration',
  'categories',
  'categoryOther',
  'addressed',
  'addressedPartiallyReason',
  'addressedNoReason',
  'furtherService',
  ...ratingQuestionKeys.map((question) => `ratings.${question.key}`),
  'signatureImage',
];

type RatingQuestionKey = (typeof ratingQuestionKeys)[number]['key'];
type RatingQuestion = { key: RatingQuestionKey; label: string };
type LawyerFeedbackRatings = Record<RatingQuestionKey, number | undefined>;

type LawyerCaseFeedbackForm = {
  duration: string;
  categories: string[];
  categoryOther: string;
  addressed: '' | 'yes' | 'partially' | 'no';
  addressedPartiallyReason: string;
  addressedNoReason: string;
  furtherService: string;
  ratings: LawyerFeedbackRatings;
  suggestions: string;
  signedAt: string;
  signatureImage: string;
};

const buildCaseWorkspacePath = (caseId: string | number) => PATH.pbl.caseDetail.replace(':caseId', String(caseId));

const getNumericCaseId = (caseId?: string) => {
  const numericCaseId = Number(caseId);

  return Number.isFinite(numericCaseId) ? numericCaseId : undefined;
};

const formatFormDate = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${year}/${month}/${day}`;
};

const createDefaultRatings = () =>
  ratingQuestionKeys.reduce(
    (ratings, question) => ({
      ...ratings,
      [question.key]: undefined,
    }),
    {} as LawyerFeedbackRatings
  );

const createDefaultValues = (): LawyerCaseFeedbackForm => ({
  duration: '',
  categories: [],
  categoryOther: '',
  addressed: '',
  addressedPartiallyReason: '',
  addressedNoReason: '',
  furtherService: '',
  ratings: createDefaultRatings(),
  suggestions: '',
  signedAt: '',
  signatureImage: '',
});

const getRatingValues = (ratings: LawyerFeedbackRatings) =>
  Object.values(ratings).filter((rating): rating is number => typeof rating === 'number' && Number.isFinite(rating));

const getAverageRating = (ratings: LawyerFeedbackRatings) => {
  const values = getRatingValues(ratings);

  if (!values.length) return undefined;

  return Math.round(values.reduce((total, rating) => total + rating, 0) / values.length);
};

const buildRatingsPayload = (ratings: LawyerFeedbackRatings, ratingQuestions: RatingQuestion[]) =>
  ratingQuestions.reduce<Record<string, number>>((payload, question) => {
    const rating = ratings[question.key];

    if (typeof rating === 'number' && Number.isFinite(rating)) {
      payload[question.label] = rating;
    }

    return payload;
  }, {});

const optionalText = (value?: string) => {
  const trimmedValue = (value || '').trim();

  return trimmedValue || undefined;
};

const optionalNumber = (value: string) => {
  const trimmedValue = value.trim();
  if (!trimmedValue) return undefined;

  const numberValue = Number(trimmedValue);

  return Number.isFinite(numberValue) ? numberValue : undefined;
};

const getMetadataString = (meeting: PblMeeting | undefined, key: string) => {
  const value = meeting?.metadata?.[key];

  return typeof value === 'string' ? value : undefined;
};

const getMeetingDisplay = (caseData: PblCase | null) => {
  const meeting = caseData?.meetings?.[0];
  const scheduledAt = meeting?.scheduled_at;
  const start = scheduledAt ? dayjs(scheduledAt) : null;

  if (!meeting || !start?.isValid()) {
    return {
      date: '-',
      time: '-',
    };
  }

  const endAt = meeting.ends_at_display || meeting.ends_at || getMetadataString(meeting, 'end_at');
  const end = endAt ? dayjs(endAt) : start.add(45, 'minute');

  return {
    date: start.format('D MMMM, YYYY'),
    time: end.isValid() ? `${start.format('HH:mm')} - ${end.format('HH:mm')}` : start.format('HH:mm'),
  };
};

const getNestedError = (errors: FieldErrors<LawyerCaseFeedbackForm>, fieldName: string) =>
  fieldName.split('.').reduce<unknown>((current, key) => {
    if (!current || typeof current !== 'object') return undefined;

    return (current as Record<string, unknown>)[key];
  }, errors);

const PblLawyerCaseFeedback = () => {
  //   @ts-ignore
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { caseId } = useParams();
  const { isMobile } = useResize();
  const numericCaseId = getNumericCaseId(caseId);
  const [caseData, setCaseData] = useState<PblCase | null>(null);
  const [isLoadingCase, setIsLoadingCase] = useState(false);
  const [signatureOpen, setSignatureOpen] = useState(false);
  const fieldAnchorsRef = useRef<Record<string, HTMLElement | null>>({});
  const item = useMemo(() => (caseData ? mapPblCaseToOcManagedCase(caseData) : null), [caseData]);
  const meetingDisplay = useMemo(() => getMeetingDisplay(caseData), [caseData]);
  const ratingQuestions = useMemo<RatingQuestion[]>(
    () => ratingQuestionKeys.map((question) => ({ key: question.key, label: t(question.labelKey) })),
    [t]
  );
  const {
    control,
    formState: { errors, isSubmitting },
    handleSubmit,
    register,
    setValue,
    watch,
  } = useForm<LawyerCaseFeedbackForm>({
    defaultValues: createDefaultValues(),
  });
  const watchedRatings = watch('ratings');
  const signedAt = watch('signedAt');
  const signatureImage = watch('signatureImage');
  const setFieldAnchor = useCallback(
    (fieldName: string) => (element: HTMLElement | null) => {
      fieldAnchorsRef.current[fieldName] = element;
    },
    []
  );
  const scrollToField = useCallback((fieldName: string) => {
    const anchor = fieldAnchorsRef.current[fieldName];
    if (!anchor) return;

    anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });

    const focusTarget = anchor.querySelector<HTMLElement>(
      'input:not([type="hidden"]), textarea, button, [tabindex]:not([tabindex="-1"])'
    );
    focusTarget?.focus({ preventScroll: true });
  }, []);
  const handleInvalidSubmit = useCallback(
    (formErrors: FieldErrors<LawyerCaseFeedbackForm>) => {
      const firstInvalidField = invalidFieldOrder.find((fieldName) => getNestedError(formErrors, fieldName));

      if (firstInvalidField) {
        scrollToField(firstInvalidField);
      }
    },
    [scrollToField]
  );

  useEffect(() => {
    if (!numericCaseId) {
      errorHandler(t('pbl.pagesHardcoded.lawyerCaseFeedback.invalidCaseId'));
      return;
    }

    let isMounted = true;
    setIsLoadingCase(true);

    getPblCase(numericCaseId)
      .then((response) => {
        if (!isMounted) return;

        if (response.data.success === false) {
          errorHandler(response.data.message || t('pbl.pagesHardcoded.lawyerCaseFeedback.failedToLoadCaseDetail'));
          return;
        }

        setCaseData(response.data.data);
      })
      .catch((error: any) => {
        if (!isMounted) return;
        errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.lawyerCaseFeedback.failedToLoadCaseDetail'));
      })
      .finally(() => {
        if (isMounted) setIsLoadingCase(false);
      });

    return () => {
      isMounted = false;
    };
  }, [numericCaseId]);

  const resolvedCaseId = caseData?.id || numericCaseId;
  const casePath = resolvedCaseId ? buildCaseWorkspacePath(resolvedCaseId) : PATH.pbl.lawyerCases;

  const submitFeedback = async (data: LawyerCaseFeedbackForm) => {
    if (!resolvedCaseId) {
      errorHandler(t('pbl.pagesHardcoded.lawyerCaseFeedback.invalidCaseId'));
      return;
    }

    const submittedCategories = Array.isArray(data.categories) ? data.categories : [];

    const payload = {
      evaluator_role: 'pbl_lawyer' as const,
      rating: getAverageRating(data.ratings),
      remarks: optionalText(data.suggestions),
      payload: {
        duration_minutes: optionalNumber(data.duration),
        categories: submittedCategories,
        category_other: submittedCategories.includes('Other') ? optionalText(data.categoryOther) : undefined,
        addressed: data.addressed || undefined,
        addressed_partially_reason:
          data.addressed === 'partially' ? optionalText(data.addressedPartiallyReason) : undefined,
        addressed_no_reason: data.addressed === 'no' ? optionalText(data.addressedNoReason) : undefined,
        further_service: data.furtherService || undefined,
        ratings: buildRatingsPayload(data.ratings, ratingQuestions),
        signed_at: data.signedAt || undefined,
        signature_image: data.signatureImage || undefined,
      },
    };

    try {
      const response = await submitPblEvaluation(resolvedCaseId, payload);

      if (response.data.success === false) {
        errorHandler(response.data.message || t('pbl.pagesHardcoded.lawyerCaseFeedback.failedToSubmitEvaluation'));
        return;
      }

      successHandler(response.data.message || t('pbl.pagesHardcoded.lawyerCaseFeedback.evaluationSubmitted'));
      navigate(PATH.pbl.lawyerCases);
    } catch (error: any) {
      errorHandler(error.response?.data?.message || error.response?.data?.data || t('pbl.pagesHardcoded.lawyerCaseFeedback.failedToSubmitEvaluation'));
    }
  };

  if (isLoadingCase || !item) {
    return (
      <PageContainer
        title={
          <TitleBar isMobile={isMobile}>
            <Button type="button" size="md" variant="primary" rounded={false} onClick={() => navigate(casePath)}>
              {t('pbl.pagesHardcoded.lawyerCaseFeedback.back')}</Button>
            <span>
              {t('pbl.pagesHardcoded.lawyerCaseFeedback.home')}<strong>-</strong>
            </span>
          </TitleBar>
        }
      >
        <FeedbackShell as="div">
          <SectionTitle>{t('pbl.pagesHardcoded.lawyerCaseFeedback.completionNoticeEvaluationForm')}</SectionTitle>
          <BodyText>{isLoadingCase ? t('pbl.pagesHardcoded.lawyerCaseFeedback.loadingCaseDetail') : t('pbl.pagesHardcoded.lawyerCaseFeedback.unableToLoadCaseDetail')}</BodyText>
        </FeedbackShell>
      </PageContainer>
    );
  }

  return (
    <PageContainer
      title={
        <TitleBar isMobile={isMobile}>
          <Button type="button" size="md" variant="primary" rounded={false} onClick={() => navigate(casePath)}>
            {t('pbl.pagesHardcoded.lawyerCaseFeedback.back')}</Button>
          <span>
            {t('pbl.pagesHardcoded.lawyerCaseFeedback.home')}<strong>-</strong>
          </span>
        </TitleBar>
      }
    >
      <FeedbackShell onSubmit={handleSubmit(submitFeedback, handleInvalidSubmit)}>
        <SectionTitle>{t('pbl.pagesHardcoded.lawyerCaseFeedback.completionNoticeEvaluationForm')}</SectionTitle>
        <BodyText>{t('pbl.pagesHardcoded.lawyerCaseFeedback.proBonoServiceCompletionNoticeEvaluationFormFor')}</BodyText>

        <SectionTitle>{t('pbl.pagesHardcoded.lawyerCaseFeedback.districtOfficeStaffInformation')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerCaseFeedback.legcoMembersOffice')}</InfoLabel>
          <InfoValue>{item.officeName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerCaseFeedback.assistingStaffNameLegcoMembersOffice')}</InfoLabel>
          <InfoValue>{item.assistingStaffName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerCaseFeedback.officeContact')}</InfoLabel>
          <InfoValue>{item.officeContact}</InfoValue>
        </InfoGrid>

        <SectionTitle>{t('pbl.pagesHardcoded.lawyerCaseFeedback.caseInformation')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerCaseFeedback.caseId')}</InfoLabel>
          <InfoValue>{item.caseId}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerCaseFeedback.enquirer')}</InfoLabel>
          <InfoValue>{item.enquirer.fullName}</InfoValue>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerCaseFeedback.hkid4Digits')}</InfoLabel>
          <InfoValue>{item.enquirer.identityPartial}</InfoValue>
        </InfoGrid>

        <SectionTitle>{t('pbl.pagesHardcoded.lawyerCaseFeedback.detailsOfThe45MinuteProBonoLegal')}</SectionTitle>
        <InfoGrid>
          <InfoLabel>{t('pbl.pagesHardcoded.lawyerCaseFeedback.date')}</InfoLabel>
          <InfoValue>{meetingDisplay.date}</InfoValue>
        </InfoGrid>

        <FieldGroup ref={setFieldAnchor('duration')}>
          <Controller
            control={control}
            name="duration"
            rules={{
              validate: (value) => {
                if (value == null || String(value).trim() === '') return true;
                const numericValue = Number(value);

                return (Number.isInteger(numericValue) && numericValue >= 0 && numericValue <= 480) || t('pbl.pagesHardcoded.lawyerCaseFeedback.enterAValidDuration');
              },
            }}
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id="service-duration"
                label={t('pbl.pagesHardcoded.lawyerCaseFeedback.durationOfProBonoServiceMinutes')}
                variant="standard"
                type="number"
                inputProps={{ min: 0, max: 480, step: 1 }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
              />
            )}
          />
        </FieldGroup>

        <Divider />

        <FieldGroup ref={setFieldAnchor('categories')}>
          <Controller
            control={control}
            name="categories"
            rules={{
              validate: (value) =>
                (Array.isArray(value) && value.length > 0) || t('pbl.pagesHardcoded.lawyerCaseFeedback.selectAtLeastOneEnquiryCategory'),
            }}
            render={({ field, fieldState }) => {
              const values = Array.isArray(field.value) ? field.value : [];

              return (
                <FormControl component="fieldset" error={!!fieldState.error}>
                  <FieldLabel as="legend">{t('pbl.pagesHardcoded.lawyerCaseFeedback.categoryOfEnquiry')}</FieldLabel>
                  <FormGroup>
                    {categories.map((category) => {
                      const isOther = category === t('pbl.pagesHardcoded.lawyerCaseFeedback.other');
                      const isChecked = values.includes(category);
                      const checkbox = (
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={isChecked}
                              onChange={(event) => {
                                const nextValues = event.target.checked
                                  ? [...values, category]
                                  : values.filter((item) => item !== category);

                                field.onChange(nextValues);
                                if (isOther && !event.target.checked) {
                                  setValue('categoryOther', '', { shouldDirty: true, shouldValidate: true });
                                }
                              }}
                            />
                          }
                          label={category}
                        />
                      );

                      if (!isOther) {
                        return <span key={category}>{checkbox}</span>;
                      }

                      return (
                        <ChoiceWithInput key={category}>
                          {checkbox}
                          {isChecked ? (
                            <ChoiceInputSlot ref={setFieldAnchor('categoryOther')}>
                              <Controller
                                control={control}
                                name="categoryOther"
                                shouldUnregister
                                rules={{
                                  validate: (value) =>
                                    Boolean(value?.trim()) || t('pbl.pagesHardcoded.lawyerCaseFeedback.enterTheOtherEnquiryCategory'),
                                }}
                                render={({ field: otherField, fieldState: otherFieldState }) => (
                                  <TextField
                                    {...otherField}
                                    label={t('pbl.pagesHardcoded.lawyerCaseFeedback.otherEnquiryCategory')}
                                    variant="standard"
                                    error={!!otherFieldState.error}
                                    helperText={otherFieldState.error?.message}
                                    fullWidth
                                  />
                                )}
                              />
                            </ChoiceInputSlot>
                          ) : null}
                        </ChoiceWithInput>
                      );
                    })}
                  </FormGroup>
                  {fieldState.error?.message && <FormHelperText>{fieldState.error.message}</FormHelperText>}
                </FormControl>
              );
            }}
          />
        </FieldGroup>

        <PartTitle>{t('pbl.pagesHardcoded.lawyerCaseFeedback.part2ServiceDetails')}</PartTitle>
        <FieldGroup ref={setFieldAnchor('addressed')}>
          <Controller
            control={control}
            name="addressed"
            rules={{ required: t('pbl.pagesHardcoded.lawyerCaseFeedback.selectAnAnswer') }}
            render={({ field, fieldState }) => (
              <FormControl component="fieldset" error={!!fieldState.error}>
                <FieldLabel as="legend">{t('pbl.pagesHardcoded.lawyerCaseFeedback.wasTheEnquiryFullyAddressed')}</FieldLabel>
                <RadioGroup value={field.value || ''} onChange={(_, value) => field.onChange(value)}>
                  <FormControlLabel value="yes" control={<Radio color="primary" />} label={t('pbl.pagesHardcoded.lawyerCaseFeedback.yes')} />
                  <ChoiceWithInput>
                    <FormControlLabel value="partially" control={<Radio color="primary" />} label={t('pbl.pagesHardcoded.lawyerCaseFeedback.partially')} />
                    {field.value === 'partially' ? (
                      <ChoiceInputSlot ref={setFieldAnchor('addressedPartiallyReason')}>
                        <Controller
                          control={control}
                          name="addressedPartiallyReason"
                          shouldUnregister
                          rules={{
                            validate: (value) =>
                              Boolean(value?.trim()) || t('pbl.pagesHardcoded.lawyerCaseFeedback.enterThePartiallyAddressedReason'),
                          }}
                          render={({ field: reasonField, fieldState: reasonFieldState }) => (
                            <TextField
                              {...reasonField}
                              label={t('pbl.pagesHardcoded.lawyerCaseFeedback.partiallyAddressedReason')}
                              variant="standard"
                              error={!!reasonFieldState.error}
                              helperText={reasonFieldState.error?.message}
                              fullWidth
                            />
                          )}
                        />
                      </ChoiceInputSlot>
                    ) : null}
                  </ChoiceWithInput>
                  <ChoiceWithInput>
                    <FormControlLabel value="no" control={<Radio color="primary" />} label={t('pbl.pagesHardcoded.lawyerCaseFeedback.no')} />
                    {field.value === 'no' ? (
                      <ChoiceInputSlot ref={setFieldAnchor('addressedNoReason')}>
                        <Controller
                          control={control}
                          name="addressedNoReason"
                          shouldUnregister
                          rules={{
                            validate: (value) => Boolean(value?.trim()) || t('pbl.pagesHardcoded.lawyerCaseFeedback.enterTheNotAddressedReason'),
                          }}
                          render={({ field: reasonField, fieldState: reasonFieldState }) => (
                            <TextField
                              {...reasonField}
                              label={t('pbl.pagesHardcoded.lawyerCaseFeedback.notAddressedReason')}
                              variant="standard"
                              error={!!reasonFieldState.error}
                              helperText={reasonFieldState.error?.message}
                              fullWidth
                            />
                          )}
                        />
                      </ChoiceInputSlot>
                    ) : null}
                  </ChoiceWithInput>
                </RadioGroup>
                {fieldState.error?.message && <FormHelperText>{fieldState.error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </FieldGroup>

        <FieldGroup ref={setFieldAnchor('furtherService')}>
          <Controller
            control={control}
            name="furtherService"
            rules={{ required: t('pbl.pagesHardcoded.lawyerCaseFeedback.selectWhetherFurtherServiceWasRequested') }}
            render={({ field, fieldState }) => (
              <FormControl component="fieldset" error={!!fieldState.error}>
                <FieldLabel as="legend">{t('pbl.pagesHardcoded.lawyerCaseFeedback.wasFurtherLegalServiceRequestedByEnquirer')}</FieldLabel>
                <RadioGroup value={field.value || ''} onChange={(_, value) => field.onChange(value)}>
                  {[
                    t('pbl.pagesHardcoded.lawyerCaseFeedback.no'),
                    t('pbl.pagesHardcoded.lawyerCaseFeedback.yesFurtherServiceProposedAtDiscountRateAnd'),
                    t('pbl.pagesHardcoded.lawyerCaseFeedback.yesFurtherServiceProposedAtDiscountRateBut'),
                  ].map((choice) => (
                    <FormControlLabel key={choice} value={choice} control={<Radio color="primary" />} label={choice} />
                  ))}
                </RadioGroup>
                {fieldState.error?.message && <FormHelperText>{fieldState.error.message}</FormHelperText>}
              </FormControl>
            )}
          />
        </FieldGroup>

        <PartTitle>{t('pbl.pagesHardcoded.lawyerCaseFeedback.part3LawyerSEvaluationOfTheScheme')}</PartTitle>
        <BodyText>{t('pbl.pagesHardcoded.lawyerCaseFeedback.pleaseRateFrom1PoorTo5Excellent')}</BodyText>
        {ratingQuestions.map((question) => (
          <RatingBlock key={question.key} ref={setFieldAnchor(`ratings.${question.key}`)}>
            <RatingLabel>{question.label} *</RatingLabel>
            <Controller
              control={control}
              name={`ratings.${question.key}`}
              rules={{ required: t('pbl.pagesHardcoded.lawyerCaseFeedback.selectAScore') }}
              render={({ field, fieldState }) => (
                <>
                  <RatingScale
                    label={question.label}
                    value={watchedRatings?.[question.key] ?? field.value}
                    onChange={(value) => field.onChange(value)}
                  />
                  {fieldState.error?.message && <ValidationText>{fieldState.error.message}</ValidationText>}
                </>
              )}
            />
          </RatingBlock>
        ))}

        <FieldGroup>
          <Controller
            control={control}
            name="suggestions"
            render={({ field, fieldState }) => (
              <TextField
                {...field}
                id="suggestions"
                label={t('pbl.pagesHardcoded.lawyerCaseFeedback.suggestionsForImprovement')}
                variant="standard"
                multiline
                minRows={2}
                error={!!fieldState.error}
                helperText={fieldState.error?.message}
                fullWidth
              />
            )}
          />
        </FieldGroup>

        <FieldGroup ref={setFieldAnchor('signatureImage')}>
          <SectionTitle>{t('pbl.pagesHardcoded.lawyerCaseFeedback.signature')}</SectionTitle>
          <input type="hidden" {...register('signedAt')} />
          <input type="hidden" {...register('signatureImage', { required: t('pbl.pagesHardcoded.lawyerCaseFeedback.signatureIsRequired') })} />
          <SignatureRow>
            <InfoValue>{t('pbl.pagesHardcoded.lawyerCaseFeedback.lawyerSSignature')}</InfoValue>
            {signatureImage ? (
              <SignedBadge>{t('pbl.pagesHardcoded.lawyerCaseFeedback.signed')}</SignedBadge>
            ) : (
              <Button type="button" size="md" variant="primary" rounded={false} onClick={() => setSignatureOpen(true)}>
                {t('pbl.pagesHardcoded.lawyerCaseFeedback.sign')}</Button>
            )}
            <InfoLabel>{t('pbl.pagesHardcoded.lawyerCaseFeedback.date2')}</InfoLabel>
            <InfoValue>{signedAt}</InfoValue>
          </SignatureRow>
          {signatureImage ? (
            <SignaturePreview>
              <SignaturePreviewImage src={signatureImage} alt={t('pbl.pagesHardcoded.lawyerCaseFeedback.lawyerSignaturePreview')} />
              <SignaturePreviewAction type="button" onClick={() => setSignatureOpen(true)}>
                {t('pbl.pagesHardcoded.lawyerCaseFeedback.reSign')}</SignaturePreviewAction>
            </SignaturePreview>
          ) : null}
          {errors.signatureImage?.message && <ValidationText>{errors.signatureImage.message}</ValidationText>}
        </FieldGroup>

        <ActionRow>
          <Button
            type="submit"
            size="lg"
            variant="primary"
            rounded={false}
            disabled={isSubmitting}
          >
            {isSubmitting ? t('pbl.pagesHardcoded.lawyerCaseFeedback.submitting') : t('pbl.pagesHardcoded.lawyerCaseFeedback.submitResponse')}
          </Button>
          <Button type="button" size="lg" variant="primary-invert" rounded={false} onClick={() => navigate(casePath)}>
            {t('pbl.pagesHardcoded.lawyerCaseFeedback.cancel')}</Button>
        </ActionRow>
      </FeedbackShell>

      <SignatureModal
        isOpen={signatureOpen}
        title={t('pbl.pagesHardcoded.lawyerCaseFeedback.lawyerSSignature2')}
        onClose={() => setSignatureOpen(false)}
        onConfirm={(dataUrl) => {
          setValue('signatureImage', dataUrl, { shouldDirty: true, shouldValidate: true });
          setValue('signedAt', formatFormDate(new Date()), { shouldDirty: true, shouldValidate: true });
        }}
        onEmptySignature={() => errorHandler(t('pbl.pagesHardcoded.lawyerCaseFeedback.pleaseProvideASignatureBeforeConfirming'))}
      />
    </PageContainer>
  );
};

export default PblLawyerCaseFeedback;
